import type { IAttribute, IItem } from '../../../IItem'
import ErrorLabel from '../../ErrorLabel'
import { ProListerSelect } from '../../../../Components/ProListerSelect/ProListerSelect'
import styles from '../CategoryPicker.module.scss'
import { useEffect, useMemo, useState } from 'react'
import { ReactComponent as MultiColorIcon } from '../../../../Icons/multicolor.svg'
import { ReactComponent as BronzeIcon } from '../../../../Icons/bronze.svg'
import { ReactComponent as SilverIcon } from '../../../../Icons/silver.svg'
import { ReactComponent as GoldIcon } from '../../../../Icons/gold.svg'
import Label from '../../../../Components/Label'
import type { ISellItem } from '../../../../SellView/ISellView'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { conditionAttributeId } from '../../../../Constants/Attributes'

interface IAttributesState {
  [categoryId: string]: IAttribute[]
}

interface IAttributesProps {
  item: IItem | ISellItem
  categoryId: string
  attributes: IAttributesState
  defaultAttributes?: IAttribute[]
  setSelectedAttributes: (a: IAttributesState, categoryId: string) => void
}

enum Ids {
  ClothingSize = 1,
  Color = 2,
  Brand = 3,
  Quantity = 6,
  Design = 7,
  Motif = 9,
}

const colorMapping = {
  multi: {},
  red: '#e70f2e',
  yellow: '#ffcf3c',
  orange: '#fb863b',
  beige: '#d4be8d',
  turquoise: '#38cbce',
  brown: '#875421',
  green: '#90d43b',
  blue: '#3f99fa',
  purple: '#9c56ff',
  pink: '#ff57cf',
  black: '#000000',
  gray: '#aaa',
  white: '#fff',
  silver: {},
  gold: {},
  bronze: {},
}

const GetIcon = ({ v }: { v: string }) => {
  switch (v) {
    case 'multi':
      return <MultiColorIcon />
    case 'bronze':
      return <BronzeIcon className="h-100" />
    case 'silver':
      return <SilverIcon className="h-100" />
    case 'gold':
      return <GoldIcon className="h-100" />
    default:
      return null
  }
}

type ToSelectableTabsProps = {
  attribute: IAttribute
  selectAction: (a: IAttribute, selectedValues: string[]) => void
  selectedValues: string[]
  displayColor: boolean
}

const ToSelectableTabs = ({
  attribute,
  selectAction,
  selectedValues,
  displayColor = false,
}: ToSelectableTabsProps) => {
  const { t } = useTranslation()
  const onSelected = (selectedValue: string) => {
    let newValues = [...selectedValues]
    if (selectedValues.includes(selectedValue)) {
      newValues = newValues.filter((x) => x !== selectedValue)
    } else {
      newValues = [...selectedValues, selectedValue]
      // only allow maxNumberOfValues, use last selected values
      if (
        attribute.maxNumberOfValues > 0 &&
        newValues.length > attribute.maxNumberOfValues
      ) {
        newValues = newValues.slice(-attribute.maxNumberOfValues)
      }
    }
    // if the new values are different from the old values, select the new values
    if (JSON.stringify(selectedValues) !== JSON.stringify(newValues)) {
      selectAction(attribute, newValues)
    }
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {attribute.possibleTermValues.map((v, i) => (
        <button
          className={classNames(
            'd-flex justify-content-center p-0',
            styles.attribute,
            selectedValues.includes(v) && styles.selected
          )}
          key={`value${v}-${i}`}
          onClick={() => onSelected(v)}
        >
          {displayColor && (
            <div
              className={classNames('h-100', styles.color)}
              style={{
                background: colorMapping[v],
              }}
            >
              <GetIcon v={v} />
            </div>
          )}
          <span className="p-1">
            {t(`attributes:attribute_af-${attribute.key}_${v}`, v)}
          </span>
        </button>
      ))}
    </div>
  )
}

type ToDisplayedAttributeProps = {
  attribute: IAttribute
  selectAction: (a: IAttribute, selectedValues: string[]) => void
  selected: { [key: string]: string[] }
}

const ToDisplayedAttribute = ({
  attribute,
  selectAction,
  selected,
}: ToDisplayedAttributeProps) => {
  const { t } = useTranslation()
  const selectedValues = selected[attribute.id] ? selected[attribute.id] : []

  if (attribute.id === Ids.Brand) {
    const defaultMultiIds =
      selectedValues && selectedValues.length > 0
        ? attribute.possibleTermValues.filter((p) => selectedValues.includes(p))
        : []
    return (
      <ProListerSelect
        options={attribute.possibleTermValues.map((p) => {
          return {
            value: p,
            label: p,
          }
        })}
        isMulti
        onChange={(o) => {
          selectAction(
            attribute,
            o.map((x) => x.value)
          )
        }}
        defaultMultiIds={defaultMultiIds}
        placeholder={t('itemsForm_attributes_search')}
      />
    )
  }
  return (
    <ToSelectableTabs
      attribute={attribute}
      selectAction={selectAction}
      selectedValues={selectedValues}
      displayColor={attribute.id === Ids.Color}
    />
  )
}

type ToDisplayedAttributeSectionProps = {
  attribute: IAttribute
  selectAction: (a: IAttribute, selectedValues: string[]) => void
  selected: { [key: string]: string[] }
  item: IItem | ISellItem
}

const ToDisplayedAttributeSection = ({
  attribute,
  selectAction,
  selected,
  item,
}: ToDisplayedAttributeSectionProps) => {
  const { t } = useTranslation()
  const selectedOnAttribute = (selected) => {
    const selectedValues = selected[attribute.id] ? selected[attribute.id] : []
    const selectedOnAttribute =
      selectedValues && selectedValues.length > 0
        ? attribute.possibleTermValues.filter((p) => selectedValues.includes(p))
        : []

    return selectedOnAttribute.length
  }
  return (
    <div>
      {(selectedOnAttribute(selected) < attribute.minNumberOfValues &&
        attribute.minNumberOfValues > 0) ||
      (Object.keys(selected).length === 0 &&
        attribute.minNumberOfValues > 0) ? (
        <ErrorLabel
          label={t('attributes:attribute_' + attribute.key)}
          description={
            t('itemsForm_attributes_errorLabelDescription') +
            ` ${attribute.minNumberOfValues}`
          }
        />
      ) : (
        <Label
          label={t('attributes:attribute_' + attribute.key)}
          description={
            attribute.maxNumberOfValues !== 0
              ? `(` +
                t('itemsForm_attributes_labelDescription') +
                ` ${attribute.maxNumberOfValues})`
              : ''
          }
        />
      )}
      <ToDisplayedAttribute
        attribute={attribute}
        selectAction={selectAction}
        selected={selected}
      />
    </div>
  )
}

export const Attributes = ({
  categoryId,
  attributes,
  setSelectedAttributes,
  defaultAttributes = [],
  item,
}: IAttributesProps) => {
  const [selected, setIsSelected] = useState({})

  const selectAction = (attribute: IAttribute, selectedValues: string[]) => {
    const newSelection = {
      ...selected,
      [attribute.id]: selectedValues,
    }
    setIsSelected(newSelection)
    setSelectedAttributes(newSelection, categoryId)
  }

  const selectedCategoryAttributes = attributes[categoryId]

  const attributesList =
    attributes && selectedCategoryAttributes ? selectedCategoryAttributes : []

  useEffect(() => {
    if (defaultAttributes.length > 0 && attributesList.length > 0) {
      let allSelected = {}

      for (let s of defaultAttributes) {
        allSelected = {
          ...allSelected,
          [s.id]: s.possibleTermValues,
        }
      }
      setIsSelected(allSelected)
    }
  }, [
    defaultAttributes,
    selectedCategoryAttributes,
    setIsSelected,
    attributesList.length,
  ])

  const sortedAttributes = useMemo(() => {
    return selectedCategoryAttributes
      ? // Cannot modify a state object directly, so we need to create a copy
        [...selectedCategoryAttributes].sort((a, b) =>
          // sort conditionAttributeId first
          a.id === conditionAttributeId
            ? -1
            : b.id === conditionAttributeId
              ? 1
              : // sort by minNumberOfValues (means that required attributes are at the top)
                b.minNumberOfValues - a.minNumberOfValues
        )
      : []
  }, [selectedCategoryAttributes])

  if (attributesList.length === 0) return null

  return (
    <>
      {sortedAttributes.map((attribute) => (
        <ToDisplayedAttributeSection
          key={`attribute-${attribute.id}-${categoryId}`}
          attribute={attribute}
          selectAction={selectAction}
          selected={selected}
          item={item}
        />
      ))}
    </>
  )
}
